import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Menu from "../Menu";
import useApiUrls from "../../useApiUrls";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const SendDQRImage = () => {
  const { merchant, pos } = useParams();
  const location = useLocation();
  const stateStoreId = location.state?.storeid || "";

  const [activeTab, setActiveTab] = useState("single");
  const [merchantname, setMerchantname] = useState(merchant || "All");
  const [storeid, setStoreid] = useState(stateStoreId || "All");
  const [posid, setPosid] = useState(pos || "0");
  const [DQRImagesid, setDQRImagesid] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [merchantList, setMerchantList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [posList, setPosList] = useState([]);
  const [DQRImagesList, setDQRImagesList] = useState([]);

  // Group states for future implementation
  const [groupList, setGroupList] = useState([]);
  const [selectedSubGroupsDQRImage, setSelectedSubGroupsDQRImage] = useState([]);
  const [subGroupDQRImageLoading, setSubGroupDQRImageLoading] = useState(false);
  const [subGroupDQRImageResponseData, setSubGroupDQRImageResponseData] = useState(null);
  const [subGroupDQRImageErrorMessage, setSubGroupDQRImageErrorMessage] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { getUrlByOperation, error: urlError } = useApiUrls();

  // Helper function to get cookie value by name
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch merchants when dropdown is focused
  const fetchMerchants = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in localStorage");
      return;
    }

    try {
      const posGetDistinctMerchantId = getUrlByOperation("posGetDistinctMerchantId");
      console.log("URL for posGetDistinctMerchantId:", posGetDistinctMerchantId);

      if (!posGetDistinctMerchantId) {
        console.error("URL for posGetDistinctMerchantId operation not found.");
        return;
      }

      const response = await axios.get(posGetDistinctMerchantId, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
        },
      });

      if (response.data && Array.isArray(response.data.data)) {
        const merchants = response.data.data.map((item) => item.merchantid);
        setMerchantList(merchants);
      }
    } catch (error) {
      console.error("Error fetching merchant list:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Fetch Store IDs based on the selected merchant
  const fetchStoresByMerchant = async (merchantId) => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in localStorage");
      return;
    }

    try {
      const response = await axios.get(
        `https://api.dqr-display-tms.eventum.co.in/api/POSOperation/GetDistinct/specificstoreidbymerchantid_active/${merchantId}`,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const stores = response.data.data.map((item) => item.storeid || item.value || item.id);
        setStoreList(stores);
      } else {
        setStoreList([]);
      }
    } catch (error) {
      console.error("Error fetching Store list:", error);
      setStoreList([]);
    }
  };

  // Fetch POS IDs based on the selected store
  const fetchPosByStore = async (storeId) => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in localStorage");
      return;
    }

    try {
      const response = await axios.get(
        `https://api.dqr-display-tms.eventum.co.in/api/POSOperation/GetDistinct/specificposbystoreid_active/${storeId}`,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const posIds = response.data.data.map((item) => ({
          id: item.id,
          value: item.value,
        }));
        setPosList(posIds);
      } else {
        setPosList([]);
      }
    } catch (error) {
      console.error("Error fetching POS list:", error);
    }
  };

  // Fetch DQRImages IDs from the new API endpoint
  const fetchDQRImagesIds = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    try {
      const response = await axios.get(
        "https://api.dqr-display-tms.eventum.co.in/api/DQROperation/GetDistinct/dqrname",
        {
          headers: {
            "accept": "text/plain",
            "UserId": userid,
            "X-Authorization": token,
          },
        }
      );

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setDQRImagesList(response.data.data);
        console.log("DQR Images data:", response.data.data);
      } else {
        setDQRImagesList([]);
        console.log("No DQR Images data found");
      }
    } catch (error) {
      console.error("Error fetching DQR Images:", error);
      setDQRImagesList([]);
    }
  };

  // Fetch groups and subgroups
  const fetchGroups = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    try {
      const response = await axios.get(
        "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/GroupAndSubGroup",
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setGroupList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  // Handle group selection (select all subgroups in a group)
  const handleGroupSelect = (groupId) => {
    const group = groupList.find((g) => g.id === groupId);
    if (!group || !group.subgroupdata) return;

    const subgroupIds = group.subgroupdata.map((sg) => sg.id);
    const allSelected = subgroupIds.every((id) =>
      selectedSubGroupsDQRImage.includes(id)
    );

    if (allSelected) {
      // Deselect all subgroups in this group
      setSelectedSubGroupsDQRImage(
        selectedSubGroupsDQRImage.filter((id) => !subgroupIds.includes(id))
      );
    } else {
      // Select all subgroups in this group
      const newSelection = [...selectedSubGroupsDQRImage];
      subgroupIds.forEach((id) => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      setSelectedSubGroupsDQRImage(newSelection);
    }
  };

  // Handle individual subgroup selection
  const handleSubGroupSelect = (subgroupId) => {
    if (selectedSubGroupsDQRImage.includes(subgroupId)) {
      setSelectedSubGroupsDQRImage(
        selectedSubGroupsDQRImage.filter((id) => id !== subgroupId)
      );
    } else {
      setSelectedSubGroupsDQRImage([...selectedSubGroupsDQRImage, subgroupId]);
    }
  };

  // Check if all subgroups in a group are selected
  const isGroupFullySelected = (groupId) => {
    const group = groupList.find((g) => g.id === groupId);
    if (!group || !group.subgroupdata || group.subgroupdata.length === 0)
      return false;

    const subgroupIds = group.subgroupdata.map((sg) => sg.id);
    return subgroupIds.every((id) => selectedSubGroupsDQRImage.includes(id));
  };

  // Check if some (but not all) subgroups in a group are selected
  const isGroupPartiallySelected = (groupId) => {
    const group = groupList.find((g) => g.id === groupId);
    if (!group || !group.subgroupdata || group.subgroupdata.length === 0)
      return false;

    const subgroupIds = group.subgroupdata.map((sg) => sg.id);
    const selectedCount = subgroupIds.filter((id) =>
      selectedSubGroupsDQRImage.includes(id)
    ).length;

    return selectedCount > 0 && selectedCount < subgroupIds.length;
  };

  // Get selected subgroup names for display
  const getSelectedSubGroupNames = () => {
    const names = [];
    groupList.forEach((group) => {
      if (group.subgroupdata) {
        group.subgroupdata.forEach((sg) => {
          if (selectedSubGroupsDQRImage.includes(sg.id)) {
            names.push(sg.name);
          }
        });
      }
    });
    return names;
  };

  // Function to handle the API call for single DQR Image send
  const handleSendDQRImages = async () => {
    setLoading(true);

    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      merchantname: merchantname,
      storeid: storeid || "All",
      posid: parseInt(posid) || 0,
      dqrid: parseInt(DQRImagesid),
    };

    try {
      const response = await axios.post(
        "https://api.dqr-display-tms.eventum.co.in/api/SendToDevice/DQR/FirstImage",
        requestBody,
        {
          headers: {
            "accept": "text/plain",
            "UserId": userid,
            "X-Authorization": token,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (response.data) {
        setResponseData(response.data);
        console.log("send   ", response.data);
        setErrorMessage("");
      } else {
        setResponseData(null);
        setErrorMessage("No response from the server");
      }

      setLoading(false);
    } catch (error) {
      console.error("Error sending DQRImage:", error);
      // Handle error responses that might have data
      if (error.response && error.response.data) {
        setResponseData(error.response.data);
        console.log("Error response data:", error.response.data);
      } else {
        setResponseData(null);
        setErrorMessage("Error sending DQR Image");
      }
      setLoading(false);
    }
  };

  // Function to handle Send DQR Image to SubGroup API call
  const handleSendDQRImageToSubGroup = async () => {
    if (selectedSubGroupsDQRImage.length === 0) {
      setSubGroupDQRImageErrorMessage("Please select at least one subgroup");
      return;
    }

    if (!DQRImagesid) {
      setSubGroupDQRImageErrorMessage("Please select a DQR Image");
      return;
    }

    setSubGroupDQRImageLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      subgroupids: selectedSubGroupsDQRImage,
      dqrid: parseInt(DQRImagesid),
    };

    try {
      const response = await axios.post(
        "https://api.dqr-display-tms.eventum.co.in/api/SendToDevice/SG/DQR/FirstImage",
        requestBody,
        {
          headers: {
            "accept": "text/plain",
            "UserId": userid,
            "X-Authorization": token,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (response.data) {
        setSubGroupDQRImageResponseData(response.data);
        setSubGroupDQRImageErrorMessage("");
        // Only clear selections on success
        if (response.data.responsestatus === "SUCCESS") {
          setSelectedSubGroupsDQRImage([]);
          setDQRImagesid("");
        }
      }

      setSubGroupDQRImageLoading(false);
    } catch (error) {
      console.error("Error sending DQR Image to subgroup:", error);
      // Handle error responses that might have data
      if (error.response && error.response.data) {
        setSubGroupDQRImageResponseData(error.response.data);
        console.log("Group error response data:", error.response.data);
        setSubGroupDQRImageErrorMessage(
          error.response.data.errormsg || "Error sending DQR Image to subgroup"
        );
      } else {
        setSubGroupDQRImageResponseData(null);
        setSubGroupDQRImageErrorMessage("Error sending DQR Image to subgroup");
      }
      setSubGroupDQRImageLoading(false);
    }
  };

  useEffect(() => {
    fetchDQRImagesIds();
    fetchGroups();
  }, []);

  // Fetch initial data if params are present
  useEffect(() => {
    if (merchantname && merchantname !== "Select" && merchantname !== "All") {
      fetchStoresByMerchant(merchantname);
    }
  }, [merchantname]);

  useEffect(() => {
    if (storeid && storeid !== "All") {
      fetchPosByStore(storeid);
    }
  }, [storeid]);

  return (
    <>
      <Menu />
      <div className="content-wrapper" style={{ padding: "20px" }}>
        {/* Tab Navigation */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
            backgroundColor: "var(--surface)",
            borderRadius: "8px",
            width: "400px",
            margin: "0 auto 20px",
            borderBottom: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <button
            style={{
              flex: 1,
              padding: "12px",
              backgroundColor:
                activeTab === "single" ? "#007bff" : "transparent",
              color: activeTab === "single" ? "#fff" : "var(--text)",
              border: "none",
              cursor: "pointer",
              borderRadius: "8px 0 0 0",
              fontWeight: activeTab === "single" ? "bold" : "normal",
              transition: "all 0.3s ease",
            }}
            onClick={() => setActiveTab("single")}
          >
            Single
          </button>
          <button
            style={{
              flex: 1,
              padding: "12px",
              backgroundColor:
                activeTab === "group" ? "#007bff" : "transparent",
              color: activeTab === "group" ? "#fff" : "var(--text)",
              border: "none",
              cursor: "pointer",
              borderRadius: "0 8px 0 0",
              fontWeight: activeTab === "group" ? "bold" : "normal",
              transition: "all 0.3s ease",
            }}
            onClick={() => setActiveTab("group")}
          >
            Group
          </button>
        </div>

        {/* Single Tab Content */}
        {activeTab === "single" && (
          <div
            style={{
              backgroundColor: "var(--surface)",
              padding: "2rem",
              borderRadius: "8px",
              width: "400px",
              margin: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <h4 style={{ marginBottom: "20px" }}>Send DQR Image</h4>
            <div className="form-group">
              <label>Merchant Name</label>
              {merchant ? (
                <select className="form-control" value={merchantname} disabled>
                  <option value={merchant}>{merchant}</option>
                </select>
              ) : (
                <select
                  className="form-control"
                  value={merchantname}
                  onChange={(e) => {
                    const selectedMerchant = e.target.value;
                    setMerchantname(selectedMerchant);
                    setStoreid("");
                    setPosid("");
                    if (selectedMerchant) {
                      if (selectedMerchant !== "All") {
                        fetchStoresByMerchant(selectedMerchant);
                      } else {
                        setStoreList([]);
                        setPosList([]);
                      }
                    } else {
                      setStoreList([]);
                      setPosList([]);
                    }
                  }}
                  onFocus={fetchMerchants}
                >
                  <option value="">Select</option>
                  <option value="All">All</option>
                  {merchantList.map((merchant, index) => (
                    <option key={index} value={merchant}>
                      {merchant}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Store ID</label>
              <select
                className="form-control"
                value={storeid}
                onChange={(e) => {
                  const selectedStore = e.target.value;
                  setStoreid(selectedStore);
                  setPosid("");
                  if (selectedStore && selectedStore !== "All") {
                    fetchPosByStore(selectedStore);
                  } else {
                    setPosList([]);
                  }
                }}
                disabled={!merchantname}
              >
                <option value="">Select Store</option>
                <option value="All">All</option>
                {storeList.map((store, index) => (
                  <option key={index} value={store}>
                    {store}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>POS ID</label>
              {pos ? (
                <select className="form-control" value={String(posid)} disabled>
                  {posList.length > 0 ? (
                    posList.map((p, index) => (
                      <option key={index} value={String(p.id)}>
                        {p.value}
                      </option>
                    ))
                  ) : (
                    <option value={String(pos)}>{pos}</option>
                  )}
                </select>
              ) : (
                <select
                  className="form-control"
                  value={posid}
                  onChange={(e) => setPosid(e.target.value)}
                  disabled={!merchantname}
                  style={{ backgroundColor: "var(--surface)", color: "var(--text)" }}
                >
                  <option value="0">All</option>
                  {posList.map((pos, index) => (
                    <option key={index} value={pos.id}>
                      {pos.value}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>DQR Image ID</label>
              <select
                className="form-control"
                value={DQRImagesid}
                onChange={(e) => setDQRImagesid(e.target.value)}
              >
                <option value="">Select DQR Image</option>
                {DQRImagesList.map((dqrImage, index) => (
                  <option key={index} value={dqrImage.id}>
                    {dqrImage.value}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
              }}
            >
              <button
                className="btn btn-primary"
                onClick={handleSendDQRImages}
                disabled={loading || !DQRImagesid}
              >
                {loading ? "Sending..." : "Send DQR Image"}
              </button>
            </div>
          </div>
        )}

        {/* Group Tab Content */}
        {activeTab === "group" && (
          <div
            style={{
              backgroundColor: "var(--surface)",
              padding: "2rem",
              borderRadius: "8px",
              width: "600px",
              margin: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <h4 style={{ marginBottom: "20px" }}>
              Send DQR Image to SubGroup
            </h4>

            <div className="form-group" style={{ position: "relative", zIndex: 100 }}>
              <label>Select Subgroups</label>
              <div ref={dropdownRef} style={{ position: "relative" }}>
                {/* Dropdown Toggle */}
                <div
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "4px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: "var(--surface)",
                    minHeight: "38px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ color: selectedSubGroupsDQRImage.length === 0 ? "var(--text-muted)" : "var(--text)" }}>
                    {selectedSubGroupsDQRImage.length === 0
                      ? "Select subgroups..."
                      : `${selectedSubGroupsDQRImage.length} subgroup(s) selected`}
                  </span>
                  <i
                    className={`fas fa-chevron-${isDropdownOpen ? "up" : "down"
                      }`}
                    style={{ color: "var(--text)" }}
                  ></i>
                </div>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "4px",
                      marginTop: "4px",
                      maxHeight: "400px",
                      overflowY: "auto",
                      zIndex: 10000,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                      backgroundColor: "var(--surface, #ffffff)",
                    }}
                  >
                    {groupList.length > 0 ? (
                      groupList.map((group) => (
                        <div key={group.id}>
                          {group.subgroupdata &&
                            group.subgroupdata.length > 0 && (
                              <div>
                                {/* Group Header */}
                                <div
                                  onClick={() => handleGroupSelect(group.id)}
                                  style={{
                                    padding: "10px 15px",
                                    backgroundColor: "var(--surface2)",
                                    borderBottom: "1px solid var(--border)",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    transition: "background-color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "var(--surface)")
                                  }
                                  onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "var(--surface2)")
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={isGroupFullySelected(group.id)}
                                    ref={(input) => {
                                      if (input) {
                                        input.indeterminate =
                                          isGroupPartiallySelected(group.id);
                                      }
                                    }}
                                    onChange={() => { }}
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      marginRight: "10px",
                                      cursor: "pointer",
                                      accentColor: "var(--info)",
                                    }}
                                  />
                                  <i
                                    className="fas fa-folder"
                                    style={{ marginRight: "8px", color: "var(--text)" }}
                                  ></i>
                                  {group.name}
                                  <span
                                    style={{
                                      marginLeft: "auto",
                                      fontSize: "12px",
                                      color: "var(--text-muted)",
                                      backgroundColor: "var(--surface)",
                                      padding: "2px 8px",
                                      borderRadius: "10px",
                                    }}
                                  >
                                    {group.subgroupdata.length}
                                  </span>
                                </div>

                                {/* Subgroups */}
                                {group.subgroupdata.map((subgroup) => (
                                  <div
                                    key={subgroup.id}
                                    onClick={() =>
                                      handleSubGroupSelect(subgroup.id)
                                    }
                                    style={{
                                      padding: "8px 15px 8px 45px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      borderBottom: "1px solid var(--border)",
                                      backgroundColor:
                                        selectedSubGroupsDQRImage.includes(
                                          subgroup.id
                                        )
                                          ? "var(--info-dim)"
                                          : "var(--surface)",
                                      transition: "background-color 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (
                                        !selectedSubGroupsDQRImage.includes(
                                          subgroup.id
                                        )
                                      ) {
                                        e.currentTarget.style.backgroundColor =
                                          "var(--surface2)";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (
                                        !selectedSubGroupsDQRImage.includes(
                                          subgroup.id
                                        )
                                      ) {
                                        e.currentTarget.style.backgroundColor =
                                          "var(--surface)";
                                      }
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedSubGroupsDQRImage.includes(
                                        subgroup.id
                                      )}
                                      onChange={() => { }}
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        marginRight: "10px",
                                        cursor: "pointer",
                                        accentColor: "var(--info)",
                                      }}
                                    />
                                    <i
                                      className="fas fa-layer-group"
                                      style={{
                                        marginRight: "8px",
                                        color: "var(--text)",
                                        fontSize: "12px",
                                      }}
                                    ></i>
                                    <span
                                      style={{
                                        fontSize: "13px",
                                        color: selectedSubGroupsDQRImage.includes(
                                          subgroup.id
                                        )
                                          ? "var(--info)"
                                          : "var(--text)",
                                      }}
                                    >
                                      {subgroup.name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "var(--text-muted)",
                        }}
                      >
                        No subgroups available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Items Display */}
              {selectedSubGroupsDQRImage.length > 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "10px",
                    backgroundColor: "var(--info-dim)",
                    borderRadius: "4px",
                    border: "1px solid var(--info)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <i
                      className="fas fa-check-circle"
                      style={{ color: "var(--info)", marginRight: "8px" }}
                    ></i>
                    <strong style={{ color: "var(--info)", fontSize: "13px" }}>
                      {selectedSubGroupsDQRImage.length} subgroup(s) selected
                    </strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      maxHeight: "100px",
                      overflowY: "auto",
                    }}
                  >
                    {getSelectedSubGroupNames().map((name, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: "var(--surface)",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          color: "var(--text)",
                          border: "1px solid var(--info)",
                        }}
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group" style={{ position: "relative", zIndex: 50 }}>
              <label>DQR Image</label>
              <select
                className="form-control"
                value={DQRImagesid}
                onChange={(e) => setDQRImagesid(e.target.value)}
              >
                <option value="">Select DQR Image</option>
                {DQRImagesList.map((dqrImage, index) => (
                  <option key={index} value={dqrImage.id}>
                    {dqrImage.value}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1.5rem",
              }}
            >
              <button
                className="btn btn-primary btn-lg"
                onClick={handleSendDQRImageToSubGroup}
                disabled={
                  subGroupDQRImageLoading ||
                  selectedSubGroupsDQRImage.length === 0 ||
                  !DQRImagesid
                }
                style={{ flex: 1 }}
              >
                {subGroupDQRImageLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2"></i>
                    Send DQR Image to SubGroup
                  </>
                )}
              </button>
            </div>

            {subGroupDQRImageErrorMessage && (
              <div
                className="alert alert-danger mt-3"
                style={{ fontSize: "14px" }}
              >
                <i className="fas fa-exclamation-circle mr-2"></i>
                {subGroupDQRImageErrorMessage}
              </div>
            )}
            {subGroupDQRImageResponseData && (
              <div
                className="alert alert-success mt-3"
                style={{ fontSize: "14px" }}
              >
                <i className="fas fa-check-circle mr-2"></i>
                DQR Image sent to subgroup(s) successfully!
              </div>
            )}
          </div>
        )}

        <section className="content">
          <div className="container-fluid">
            <div className="row" style={{ marginTop: "20px" }}>
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header" style={{ backgroundColor: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
                    <h3 className="card-title" style={{ color: "var(--text)" }}>
                      <i className="fas fa-chart-line mr-2"></i>
                      DQR Image Response Results
                    </h3>
                  </div>
                  <div className="card-body table-responsive p-0">
                    <table className="table table-hover table-striped mb-0">
                      <thead style={{ backgroundColor: "var(--surface2)", borderBottom: "2px solid var(--border)" }}>
                        <tr>
                          <th style={{ color: "var(--text)", fontWeight: "600" }}>Type</th>
                          <th style={{ color: "var(--text)", fontWeight: "600" }}>Merchant Name</th>
                          <th style={{ color: "var(--text)", fontWeight: "600" }}>Store ID</th>
                          <th style={{ color: "var(--text)", fontWeight: "600" }}>POS Name</th>
                          <th style={{ color: "var(--text)", fontWeight: "600" }}>Response Status</th>
                          <th style={{ color: "var(--text)", fontWeight: "600" }}>Message/Error</th>
                          <th style={{ color: "var(--text)", fontWeight: "600" }}>Status Badge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Display Single DQR Image Results */}
                        {responseData && (
                          <>
                            {responseData.sendresult && responseData.sendresult.length > 0 ? (
                              responseData.sendresult.map((result, index) => (
                                <tr key={`single-${index}`}>
                                  <td>
                                    <span className="badge badge-info">Single</span>
                                  </td>
                                  <td>{result.merchantname || "-"}</td>
                                  <td>{result.storeid || "-"}</td>
                                  <td>{result.posname || "-"}</td>
                                  <td>{result.responsestatus || "-"}</td>
                                  <td>{result.errormsg || "-"}</td>
                                  <td>
                                    {result.responsestatus === "SUCCESS" ? (
                                      <span className="badge badge-success">
                                        ✓ Success
                                      </span>
                                    ) : (
                                      <span className="badge badge-danger">
                                        ✗ Failed
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              // Display main response when sendresult is null or empty
                              <tr key="single-main">
                                <td>
                                  <span className="badge badge-info">Single</span>
                                </td>
                                <td>{merchantname || "-"}</td>
                                <td>{storeid || "All"}</td>
                                <td>{posid || "All"}</td>
                                <td>{responseData.responsestatus || "-"}</td>
                                <td>{responseData.errormsg || "-"}</td>
                                <td>
                                  {responseData.responsestatus === "SUCCESS" ? (
                                    <span className="badge badge-success">
                                      ✓ Success
                                    </span>
                                  ) : (
                                    <span className="badge badge-danger">
                                      ✗ Failed
                                    </span>
                                  )}
                                </td>
                              </tr>
                            )}
                          </>
                        )}

                        {/* Display SubGroup DQR Image Response Data */}
                        {subGroupDQRImageResponseData && (
                          <>
                            {subGroupDQRImageResponseData.sendresult && subGroupDQRImageResponseData.sendresult.length > 0 ? (
                              subGroupDQRImageResponseData.sendresult.map(
                                (result, index) => (
                                  <tr key={`subgroup-${index}`}>
                                    <td>
                                      <span className="badge badge-secondary">
                                        SubGroup
                                      </span>
                                    </td>
                                    <td>{result.merchantname || "-"}</td>
                                    <td>{result.storeid || "-"}</td>
                                    <td>{result.posname || "-"}</td>
                                    <td>{result.responsestatus || "-"}</td>
                                    <td>{result.errormsg || "-"}</td>
                                    <td>
                                      {result.responsestatus === "SUCCESS" ? (
                                        <span className="badge badge-success">
                                          ✓ Success
                                        </span>
                                      ) : (
                                        <span className="badge badge-danger">
                                          ✗ Failed
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              // Display main response when sendresult is null or empty
                              <tr key="subgroup-main">
                                <td>
                                  <span className="badge badge-secondary">
                                    SubGroup
                                  </span>
                                </td>
                                <td>Multiple</td>
                                <td>Multiple</td>
                                <td>{selectedSubGroupsDQRImage.length} SubGroups</td>
                                <td>{subGroupDQRImageResponseData.responsestatus || "-"}</td>
                                <td>{subGroupDQRImageResponseData.errormsg || "-"}</td>
                                <td>
                                  {subGroupDQRImageResponseData.responsestatus === "SUCCESS" ? (
                                    <span className="badge badge-success">
                                      ✓ Success
                                    </span>
                                  ) : (
                                    <span className="badge badge-danger">
                                      ✗ Failed
                                    </span>
                                  )}
                                </td>
                              </tr>
                            )}
                          </>
                        )}

                        {/* Show empty state when no data */}
                        {!responseData &&
                          !subGroupDQRImageResponseData && (
                            <tr>
                              <td
                                colSpan="7"
                                style={{
                                  textAlign: "center",
                                  padding: "40px 20px",
                                }}
                              >
                                <i
                                  className="fas fa-inbox"
                                  style={{
                                    fontSize: "3rem",
                                    color: "var(--text-muted)",
                                    marginBottom: "10px",
                                  }}
                                ></i>
                                <p style={{ color: "var(--text-muted)", marginBottom: 0 }}>
                                  {errorMessage ||
                                    subGroupDQRImageErrorMessage ||
                                    "No data available yet. Send a DQR Image to see results here."}
                                </p>
                              </td>
                            </tr>
                          )}

                        {/* Show message when all are empty but we tried to send */}
                        {responseData &&
                          responseData.sendresult &&
                          responseData.sendresult.length === 0 &&
                          !subGroupDQRImageResponseData && (
                            <tr>
                              <td
                                colSpan="6"
                                style={{
                                  textAlign: "center",
                                  padding: "20px",
                                  color: "var(--text-muted)",
                                }}
                              >
                                No results to display
                              </td>
                            </tr>
                          )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SendDQRImage;
